import React, { Fragment, useMemo, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import { useRouter, Link } from 'found';
import MuiAppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import NoSsr from '@mui/material/NoSsr';
import Tooltip from 'ripple/Components/Tooltip';
import AccountCircle from '@mui/icons-material/AccountCircle';
import ExitToApp from '@mui/icons-material/ExitToApp';
import Notifications from '@mui/icons-material/Notifications';
import MenuOutlined from '@mui/icons-material/MenuOutlined';
import LocalActivity from '@mui/icons-material/LocalActivity';
// import Loyalty from '@mui/icons-material/Loyalty';
import useIsMobile from 'lib/useIsMobile';
import RSLogoLight from 'assets/RS_logo-white.png';
import { useAuthContext } from 'AuthContext';
import { useTheme } from '@mui/material';
import { useApolloClient } from '@apollo/client';
import styles from './AppBar.scss';
import { GET_PROFILE_INFO } from '../../../schema/apolloQueries/commonQueries';

const useStyles = makeStyles(() => {
  return {
    popper: {
      pointerEvents: 'all',
    },
    popperMenu: {
      fontWeight: 500,
    },
    listItemIcon: {
      minWidth: 30,
    },
    icon: {
      color: '#000',
    },
    signInBtn: {
      textTransform: 'capitalize!important',
      fontWeight: '600!important',
    },
    menuText: {
      color: '#000',
    },
  };
});

const AppBar = ({
  elevation = 0,
  position = 'fixed',
  rootClass = 'transparentAppBar',
  openLeftDrawer,
}) => {
  const theme = useTheme();
  const mobile = useIsMobile();
  const classes = useStyles();
  const {
    showSignOutDialog,
    showSignInDialog,
    // setShowLoyaltyMessage,
  } = useAuthContext();
  const {
    match: {
      location: { pathname },
    },
    router,
  } = useRouter();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const client = useApolloClient();

  const data = client.readQuery({
    query: GET_PROFILE_INFO,
  });

  const handleToggle = event => {
    event.preventDefault();
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      const {
        target: { textContent },
      } = event;
      switch (textContent) {
        case 'My Profile':
          router.push('/profile');
          break;
        case 'Sign Out':
          showSignOutDialog();
          break;
        default:
          break;
      }
      return;
    }
    setOpen(false);
  };
  const handleListKeyDown = event => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    }
  };

  // const loyaltyListener = () => {
  //   if (!data?.getProfile?.loggedIn) {
  //     setShowLoyaltyMessage(true);
  //   }
  //   return false;
  // };

  const profile = useMemo(() => {
    if (data?.getProfile?.loggedIn && data?.getProfile?.personInfo) {
      return (
        <Avatar
          style={{
            display: `${
              data?.getProfile?.personInfo?.headshot
                ? 'table-cell'
                : 'inline-flex'
            }`,
            border: '3px solid green',
          }}
          alt={data?.getProfile?.personInfo?.first}
          src={data?.getProfile?.personInfo?.headshot}
        >
          {data?.getProfile?.personInfo?.first
            && data?.getProfile?.personInfo?.first[0]}
        </Avatar>
      );
    }
    return <AccountCircle />;
  }, [data?.getProfile]);

  const menuItems = [
    {
      label: 'Events',
      link: '/discover/events',
    },
    {
      label: 'Brands',
      link: '/discover/communities',
    },
    {
      label: 'Products',
      link: '/discover/products',
    },
  ];

  const menus = menuItems.map(
    useCallback(
      m => (
        <Link
          className={
            styles[pathname === m.link ? 'selectedMenuItem' : 'menuItem']
          }
          to={m.link}
          color='inherit'
          underline='none'
        >
          {m.label}
        </Link>
      ),
      [pathname],
    ),
  );

  return (
    <MuiAppBar
      classes={{ root: styles[rootClass] }}
      elevation={elevation}
      position={position}
    >
      <Toolbar classes={{ root: styles.toolbar }} disableGutters={mobile}>
        <IconButton onClick={openLeftDrawer} size='large'>
          <MenuOutlined htmlColor={theme.palette.common.white} />
        </IconButton>
        <Link to='/'>
          <img
            className={styles.logo}
            src={RSLogoLight}
            alt='RippleStreet logo'
          />
        </Link>
        <NoSsr>
          <Typography className={styles.menuBar}>
            {!mobile && (
              <Fragment>
                {menus}
                {/* <Link
                  className={styles.menuItem}
                  to='/loyalty/rewards'
                  onClick={loyaltyListener}
                  color='inherit'
                  underline='none'
                >
                  <Badge color='secondary'>Loyalty Program</Badge>
                </Link> */}
              </Fragment>
            )}
          </Typography>
          <Typography className={styles.actionBar}>
            {/* {mobile ? (
              <Link
                className={styles.menuItem}
                to='/loyalty/rewards'
                onClick={loyaltyListener}
                color='inherit'
                underline='none'
              >
                <Badge color='secondary'>
                  <Loyalty />
                </Badge>
              </Link>
            ) : (
              ''
            )} */}
            {data?.getProfile?.loggedIn && (
              <Fragment>
                <Link
                  className={styles.menuItem}
                  to='/dashboard'
                  color='inherit'
                  underline='none'
                >
                  {mobile ? <LocalActivity /> : 'Dashboard'}
                  {/* <Badge badgeContent={person.incompleteActivityCount} color='secondary'>
                  </Badge> */}
                </Link>
                {/* <Link className={styles.menuItem} to='/activities' color='inherit' underline='none'>
                  <Badge badgeContent={person.incompleteActivityCount} color='secondary'>
                    {mobile ? <LocalActivity /> : 'Activities'}
                  </Badge>
                </Link> */}
                <Link
                  className={styles.menuItem}
                  to='/notifications'
                  color='inherit'
                  underline='none'
                >
                  <Badge
                    badgeContent={data?.getProfile?.totalNotificationCount}
                    color='primary'
                  >
                    <Notifications />
                  </Badge>
                </Link>
                <Link
                  className={styles.headshotMenuItem}
                  to='/profile'
                  color='inherit'
                  aria-controls={open ? 'menu-list-grow' : undefined}
                  aria-haspopup='true'
                  onClick={handleToggle}
                >
                  <Tooltip
                    classes={{ popper: classes.popper }}
                    placement='bottom-end'
                    open={open}
                    arrow
                    title={(
                      <ClickAwayListener onClickAway={handleClose}>
                        <MenuList
                          ref={anchorRef}
                          anchorEl={anchorRef.current}
                          autoFocusItem={open}
                          id='menu-list-grow'
                          onKeyDown={handleListKeyDown}
                        >
                          <MenuItem
                            className={classes.popperMenu}
                            onClick={handleClose}
                          >
                            <ListItemIcon
                              classes={{ root: classes.listItemIcon }}
                            >
                              <AccountCircle
                                color='inherit'
                                className={classes.icon}
                              />
                            </ListItemIcon>
                            <Typography
                              classes={{ root: classes.menuText }}
                              variant='inherit'
                            >
                              My Profile
                            </Typography>
                          </MenuItem>
                          <MenuItem
                            className={classes.popperMenu}
                            onClick={handleClose}
                          >
                            <ListItemIcon
                              classes={{ root: classes.listItemIcon }}
                            >
                              <ExitToApp className={classes.icon} />
                            </ListItemIcon>
                            <Typography
                              classes={{ root: classes.menuText }}
                              variant='inherit'
                            >
                              Sign Out
                            </Typography>
                          </MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    )}
                  >
                    {profile}
                  </Tooltip>
                </Link>
              </Fragment>
            )}
            {pathname !== '/sign-in' && !data?.getProfile?.loggedIn && (
              <>
                <Button
                  variant='contained'
                  color={
                    theme.palette.mode === 'dark' ? 'primary' : 'secondary'
                  }
                  classes={{ root: classes.signInBtn }}
                  onClick={showSignInDialog}
                >
                  Sign In
                </Button>
              </>
            )}
          </Typography>
        </NoSsr>
      </Toolbar>
    </MuiAppBar>
  );
};

AppBar.propTypes = {
  elevation: PropTypes.number,
  rootClass: PropTypes.string,
  position: PropTypes.oneOf([
    'fixed',
    'static',
    'sticky',
    'absolute',
    'relative',
  ]),
  openLeftDrawer: PropTypes.func.isRequired,
};

export default AppBar;
